// The band's own arithmetic: what renders, how wide it comes out, and what a drag actually means.
//
// None of this touches React or the API. The editor is a picture of a public surface, so every
// question it has to answer — is this partner live, does the row still fit, which partners did that
// drop renumber — is a function of the stored list and today's date, and is tested as one.
//
// The geometry and the ordering rules are shared with the event sponsor band
// (components/Common/SponsorBand/bandLayout): this module binds them to the system taxonomy and
// adds what only a system partner has — a contract that ends, a market, a "hidden by hand".

import { PARTNER_TIERS } from "./partnerPayload"
import { asRows, byOrderThenName, createBandLayout } from "components/Common/SponsorBand/bandLayout"

export {
  PLATE_WIDTH,
  PLATE_FLOOR,
  PLATE_GAP,
  TIER_GAP,
  BAND_WIDTH,
} from "components/Common/SponsorBand/bandLayout"

const MS_PER_DAY = 86400000

// A contract inside this many days is worth flagging while there is still time to renew it.
export const EXPIRING_SOON_DAYS = 45

const dayNumber = (year, month, day) => Math.round(Date.UTC(year, month, day) / MS_PER_DAY)

// A contract ends on a calendar date, and Mongo stores that date as UTC midnight — so it has to be
// read back in UTC. Reading it locally would move the date a day for anyone west of Greenwich and
// retire their partners a day early.
const contractDay = value => {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return dayNumber(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
}

// Today, on the other hand, is the admin's own today: whatever the calendar on their wall says.
const localDay = value => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return dayNumber(date.getFullYear(), date.getMonth(), date.getDate())
}

/** Whole days from today until the contract ends. Negative once it has passed, null with no date. */
export const daysLeft = (endsAt, now = new Date()) => {
  const ends = contractDay(endsAt)
  const today = localDay(now)
  if (ends === null || today === null) return null
  return ends - today
}

// The end date is inclusive: a contract ending today is still live today and drops off tomorrow.
export const isExpired = (partner, now = new Date()) => {
  const left = daysLeft(partner?.endsAt, now)
  return left !== null && left < 0
}

export const isExpiring = (partner, now = new Date()) => {
  const left = daysLeft(partner?.endsAt, now)
  return left !== null && left >= 0 && left <= EXPIRING_SOON_DAYS
}

export const isLimited = partner => Boolean(partner?.countries?.length)

/**
 * Off the band — hidden by hand or out of contract. The public band renders neither, which is the
 * whole reason this screen needs a zone for them: otherwise they are invisible in their own editor.
 */
export const isOffBand = (partner, now = new Date()) =>
  partner?.active === false || isExpired(partner, now)

export const offBandReason = (partner, now = new Date()) =>
  isExpired(partner, now) ? "expired" : "hidden"

// The system band: four fixed tiers, the tier in `tier`, and a partner is live unless its contract
// has run out or someone took it off the band.
const band = createBandLayout({
  tiers: PARTNER_TIERS,
  tierField: "tier",
  isLive: (partner, now) => !isOffBand(partner, now),
})

/**
 * How wide the band comes out at a given plate size. Empty tiers cost nothing: the public band skips
 * them entirely, so four tiers with one partner each are narrower than two tiers with three.
 */
export const bandWidth = band.bandWidth

/**
 * The capacity warning. Three outcomes, because they mean three different things to whoever is
 * adding a logo: it fits as designed, it only fits by shrinking every plate, or the band wraps onto
 * a second row and stops looking like one band.
 */
export const bandCapacity = band.bandCapacity

/**
 * The band as the editor draws it: all four tiers, every one of them a drop target, each holding the
 * partners that are live right now in display order (`rows`). Unlike the public band, an empty tier
 * is kept — a tier you cannot see is a tier you cannot drop into.
 */
export const bandTiers = band.bandTiers

/**
 * The partners the band will not draw, grouped the way the band would have grouped them — the zone
 * prints each one's tier, so reading it in tier order is the only order that scans.
 */
export const offBandPartners = (partners, now = new Date()) => {
  const tierIndex = tier => {
    const at = PARTNER_TIERS.findIndex(({ key }) => key === tier)
    return at === -1 ? PARTNER_TIERS.length : at
  }

  return asRows(partners)
    .filter(partner => isOffBand(partner, now))
    .sort((a, b) => tierIndex(a.tier) - tierIndex(b.tier) || byOrderThenName(a, b))
}

/**
 * What a drop means.
 *
 * Given the whole stored list and one move — this partner, into that tier, landing before that plate
 * (or last, when `beforeId` is null) — returns the list as it will read and the partners that have
 * to be written back. Only genuinely changed rows come back: every write is its own PATCH through
 * the broker, so renumbering a tier that did not move would be pure noise.
 *
 * Both tiers are renumbered contiguously from 1, hidden and expired partners included. Those are not
 * on the band and cannot be dragged, but they still carry an `order` that decides where they land
 * when someone restores them — leaving gaps there makes a restore reappear in an arbitrary place.
 */
export const movePartner = (partners, move) => {
  const { rows, writes } = band.moveRow(partners, move)
  return { partners: rows, writes }
}

/**
 * The keyboard path to the same move. Drag is the fast way; this is the way that works without a
 * mouse, and it is the only way to reach a plate that is off the band and has nothing to drag.
 */
export const shiftPartner = (partners, id, direction, now = new Date()) => {
  const { rows, writes } = band.shiftRow(partners, id, direction, now)
  return { partners: rows, writes }
}

/**
 * Putting a partner back on the band. Un-hiding is enough for one hidden by hand; a partner whose
 * contract has run out needs the end date gone too, or it falls straight back off the band and the
 * Restore button reads as broken.
 */
export const restoredPartner = (partner, now = new Date()) => ({
  ...partner,
  active: true,
  ...(isExpired(partner, now) ? { endsAt: "" } : {}),
})

/** The order a partner takes when it joins a tier: last, behind everything already there. */
export const nextOrderInTier = band.nextOrderInTier

/**
 * The order a saved form should carry. The form has no order field, and a write is the whole
 * partner — so without this an edit would send `order: 0` and jerk the partner to the front of its
 * tier. An edit that stays in its tier keeps its place; anything arriving in a tier goes last.
 */
export const orderForForm = band.orderForForm
